/**
 * Reading a number a visitor typed into a calculator, without letting nonsense
 * reach the answer (FRIDGE-384).
 *
 * THE DEFECT THIS REPLACES
 * ------------------------
 * Ninety tool clients shared one idiom:
 *
 *     const size = parseFloat(fridgeSize)
 *     if (!size) return null
 *
 * `!size` is false for every negative number, so `-25` passed the guard and the
 * arithmetic ran on it. `/tools/dual-compressor-refrigerator-calculator` then
 * rendered, live and reproduced:
 *
 *     "Payback in −30.8 years (10-yr savings $−130)"
 *
 * and `0.0001` — also truthy — rendered "Payback in 7,692,307.7 years", because
 * a near-zero denominator is a division by almost nothing.
 *
 * WHY `min={0}` ON THE INPUT IS NOT THE FIX
 * -----------------------------------------
 * `min` constrains the spinner arrows and marks the field `:invalid`. It does
 * not stop a typed or pasted value: the string still reaches `onChange`, still
 * lands in state, and still reaches the compute. The browser hint is worth
 * having — a `min` alongside these readers is the right pairing — but the guard
 * has to live in the compute or it is not a guard.
 *
 * WHY A SHARED READER RATHER THAN 47 LOCAL FIXES
 * ---------------------------------------------
 * The rule ("a physical quantity is finite and greater than zero") is identical
 * in every tool, and 47 copies of a rule is 47 chances to write it as `>= 0` or
 * to forget `Number.isFinite`. One reader also gives the guard test a single
 * symbol to assert against, which is what makes the ratchet possible.
 *
 * WHY TWO READERS AND NOT ONE
 * ---------------------------
 * Six fields across four tools are TEMPERATURES, where a negative is a real
 * reading a visitor legitimately types — `cooking-temperature-converter`,
 * `kegerator-beer-line-calculator`, `refrigerator-temperature-recovery-calculator`
 * (×2) and `walk-in-cooler-calculator` (×2). A blanket positive-only sweep
 * would have broken all six, which is precisely why the original QA sweep left
 * the whole class alone rather than fixing it wrongly. `readTemperature` keeps
 * them working while still rejecting the empty string and the non-finite.
 *
 * HOW THIS RELATES TO `safeParseNumber` IN `lib/calculator-utils.ts`
 * ----------------------------------------------------------------
 * That helper already existed and 19 tool clients use it. It is NOT the same
 * thing, and the difference is the reason this module exists rather than a
 * fourth argument being added to it: **`safeParseNumber` CLAMPS, it does not
 * reject.**
 *
 *     safeParseNumber('-25', 100, 30000)  →  100
 *
 * Measured, not inferred. So in `refrigerator-cost-of-ownership-calculator`,
 * which reads `safeParseNumber(purchasePrice, 100, 30000)` behind
 * `if (!price || …) return null`, a visitor who types `-25` is answered as
 * though they had typed `100`: the guard cannot fire, because `100` is truthy.
 * That is a quieter fault than "Payback in −30.8 years" and a worse one — the
 * tool states a confident answer to a question nobody asked. Its one accidental
 * save is a `min` of exactly 0, where the clamp lands on `0` and the falsy
 * guard then catches it, which is why the behaviour looks correct in some tools
 * and not others.
 *
 * These readers therefore REJECT and never substitute. A field a visitor filled
 * in wrongly shows no answer, which is honest, instead of an answer to different
 * numbers, which is not. `safeParseNumber` is superseded for reading visitor
 * input and should not be used in new tools; converting the 19 existing call
 * sites is separate work, because changing that function in place would alter
 * 19 tools at once without a per-field review. Its clamping is pinned by a test
 * in `tests/tools-numeric-input.test.ts` so the distinction cannot be lost.
 *
 * ON `Number` RATHER THAN `parseFloat`
 * -----------------------------------
 * `parseFloat` is lenient in a way that hides mistakes: `parseFloat('25abc')` is
 * `25`, and `parseFloat('1e999')` is `Infinity`. `Number` rejects trailing junk
 * outright. Its one trap is that `Number('')` and `Number('   ')` are `0`, not
 * `NaN` — so the empty string is checked before the conversion, never after.
 */

/** Bounds a caller may narrow per field. Both are inclusive. */
export interface NumericBounds {
  min?: number
  max?: number
}

/**
 * The raw string is unusable as a number: empty, whitespace-only, or not a
 * complete numeric literal. Kept separate from the range check so both readers
 * share exactly one parse.
 */
function parseStrict(raw: string): number | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  // `Number('')` is 0. Without this line an empty field reads as a real zero.
  if (trimmed.length === 0) return null
  const value = Number(trimmed)
  // Catches NaN from trailing junk AND ±Infinity from '1e999'.
  if (!Number.isFinite(value)) return null
  return value
}

/**
 * Read a PHYSICAL QUANTITY — a size, a price, a rate, a duration, a count.
 *
 * Returns null unless the value is a finite number strictly greater than zero
 * and inside `bounds`. Zero is rejected as well as negatives: every field this
 * guards is a quantity that cannot meaningfully be zero, and zero is the value
 * that produces the division-by-almost-nothing results.
 *
 * Pass `min` whenever the field has a real-world floor — a fridge cabinet is
 * not 0.0001 cu ft — because that is what turns "not negative" into "not
 * absurd". `max` bounds the other end for the same reason.
 */
export function readQuantity(raw: string, bounds: NumericBounds = {}): number | null {
  const value = parseStrict(raw)
  if (value === null) return null
  if (value <= 0) return null
  if (bounds.min !== undefined && value < bounds.min) return null
  if (bounds.max !== undefined && value > bounds.max) return null
  return value
}

/**
 * Read a WHOLE-NUMBER COUNT — people in a household, months between filter
 * changes, shelves, doors.
 *
 * Same rules as `readQuantity`, plus: the value must be an integer. It replaces
 * `parseInt`, which is lenient in the direction that hides mistakes —
 * `parseInt('4.5')` is `4`, so a visitor who typed 4.5 people was answered as
 * though they had typed 4. Rejecting is the same principle as everywhere else in
 * this module: show no answer rather than an answer to a different question.
 *
 * `Math.floor` was deliberately not used for that reason — it is the clamping
 * fault in miniature.
 */
export function readCount(raw: string, bounds: NumericBounds = {}): number | null {
  const value = readQuantity(raw, bounds)
  if (value === null) return null
  if (!Number.isInteger(value)) return null
  return value
}

/**
 * Read a NON-NEGATIVE AMOUNT — a value where zero is a real answer but a
 * negative is not: a $0 rebate, a 0% promotional APR, a brand-new appliance's
 * age of 0, a $0 price premium for smart features.
 *
 * WHY THIS IS NOT `readQuantity` WITH A `?? 0` FALLBACK (FRIDGE-411). That
 * pattern is correct for an OPTIONAL ADDITIVE field — a rebate that is simply
 * absent contributes nothing, and 0 is the neutral element of the sum, so
 * folding a rejection into 0 changes no answer. It is wrong wherever zero is a
 * MEANINGFUL reading, because `?? 0` maps a REJECTED value onto that same
 * meaningful zero. A visitor who types a premium of -500 would be answered as
 * though the smart model cost nothing extra, and the tool would report that the
 * features pay for themselves instantly. That is this card's defect wearing a
 * different helper.
 *
 * WHY IT IS NOT `readTemperature` WITH `min: 0`. Numerically identical; the
 * name would be a lie at every call site, and the reason a reader reaches for
 * the right one is that its name says what the field is.
 */
export function readNonNegative(raw: string, bounds: NumericBounds = {}): number | null {
  const value = parseStrict(raw)
  if (value === null) return null
  if (value < 0) return null
  if (bounds.min !== undefined && value < bounds.min) return null
  if (bounds.max !== undefined && value > bounds.max) return null
  return value
}

/**
 * Read a TEMPERATURE, where a negative is a legitimate reading.
 *
 * Still rejects the empty string and the non-finite, so a blank field never
 * reads as 0 °F. Supply `bounds` per field and per unit — this reader cannot
 * know whether it is being handed Celsius or Fahrenheit, so it deliberately
 * applies no default range rather than guessing one and rejecting real input.
 */
export function readTemperature(raw: string, bounds: NumericBounds = {}): number | null {
  const value = parseStrict(raw)
  if (value === null) return null
  if (bounds.min !== undefined && value < bounds.min) return null
  if (bounds.max !== undefined && value > bounds.max) return null
  return value
}
